import backgroundAudioStatus from './BackgroundAudioStatus';

/**
 * Available contrast options for the UI.
 *
 * @readonly
 * @enum {string}
*/

export const contrastOptions = {
    NORMAL: 'normal',
    INVERTED: 'inverted',
    YELLOW_ON_BLACK: 'yellow-on-black',
    YELLOW_ON_BLUE: 'yellow-on-blue',
    BLACK_ON_CREAM: 'black-on-cream',
    BLACK_ON_LIGHT_BLUE: 'black-on-light-blue',
};

const zoomFactors = {
    SMALL: 'zoom-small',
    MEDIUM: 'zoom-medium',
    LARGE: 'zoom-large',
};

/**
 * testTakerLocalStorageKeys
 *
 * Keys used for storing test-taker–specific UI and player data in localStorage.
 *
 * These keys identify *user-scoped* storage entries. They are typically wrapped
 * by an utility such as `utilService.getUserSpecificLocalStorageKey()` to ensure
 * per-user separation.
 *
 * Current usage:
 * - `PLAYER_CONTRAST`: Saves per-user contrast preferences.
 * - `PLAYER_ZOOM_FACTOR`: Saves per-user zoom-factor settings.
 * - `AUDIO_POSITION`: Saves the last-played audio position per user (if applicable).
 *
 * Values stored under these keys generally follow this structure:
 * {
 *   assessmentRunId: string | number,
 *   userId: string | number,
 *   <settingKey>: any     // e.g., contrastOption: 'normal', zoomFactor: 'small'
 * }
 *
 * @readonly
 * @enum {string}
*/

const testTakerLocalStorageKeys = {
    PLAYER_CONTRAST: 'player-contrast',
    PLAYER_ZOOM_FACTOR: 'player-zoom-factor',
    AUDIO_POSITION: 'audio-position',
};

/**
 * UI_OPTIONS_LIST
 * Enum-like list of supported UI option identifiers.
 * These correspond to user-facing settings, not storage keys.
 *
 * @readonly
 * @enum {string}
*/

export const UI_OPTIONS_LIST = {
    CONTRAST_OPTION: 'CONTRAST_OPTION',
    ZOOM_FACTOR: 'ZOOM_FACTOR',
};

export const BACKGROUND_AUDIO_SUB_HEADER_UI_DATA = {
    [backgroundAudioStatus.PAUSED]: {
        icon: 'fa fa-pause',
        caption: 'header.backgroundAudioIsPaused',
    },
    [backgroundAudioStatus.ENDED]: {
        icon: 'fa fa-volume-off',
        caption: 'header.backgroundAudiohasFinished',
    },
};

export const pbRenderThemes = ['ceq', 'fjord'];

export default {
    BREAKPOINT_MOBILE: 750,
    BREAKPOINT_DESKTOP: 1280,
    ZOOM_FACTOR: {
        SMALL: zoomFactors.SMALL,
        MEDIUM: zoomFactors.MEDIUM,
        LARGE: zoomFactors.LARGE,
    },
    CONTRAST_OPTIONS: [
        contrastOptions.NORMAL,
        contrastOptions.INVERTED,
        contrastOptions.YELLOW_ON_BLACK,
        contrastOptions.BLACK_ON_CREAM,
        contrastOptions.BLACK_ON_LIGHT_BLUE,
    ],
    VIEW_SIZE: { MOBILE: 'mobile', TABLET: 'tablet', DESKTOP: 'desktop' },
    ORIENTATION: { LANDSCAPE: 'landscape', PORTRAIT: 'portrait' },
    GAP_MATCH_SELECTOR_ID: 'gap-match-token-portal',
    GAP_MATCH_SCROLL_CLASS: 'gap-match-scroll-adjustment',
    SCALABLE_INTERACTION_IMAGE_CLASS: 'supports-image-scaling',
    PLAYER_LOADER_SELECTOR_ID: 'loader-container',
    TRUNCATED_ANNOTATION_LENGTH: 100,
    TEST_PLAYER_STYLES: ['primary_school_1', 'primary_school_2'],
    DARK_CONTRAST_COLORS: [
        contrastOptions.YELLOW_ON_BLACK,
    ],
    SECTION_LAYOUT: {
        BASIC_LEFT: 'basic-left',
        BASIC: 'basic',
        VERTICAL_LEFT: 'vertical-left',
        VERTICAL_TOP: 'vertical-top',
        VERTICAL: 'vertical',
    },
    STIMULUS_POSITION: {
        TOP: 'top',
        LEFT: 'left',
    },
    STIMULUS_HOLDER: {
        SECTION: 'section',
        QUESTION: 'question',
    },
    STIMULUS_STATE: {
        DRAG: 'drag',
        DROP: 'drop',
    },
    ANSWER_KEYS: {
        ANY_ORDER: 'IN ANY ORDER',
    },
    DEFAULT_STIMULUS_SIZE: {
        STIMULUS_SIZE: 50,
    },

    TEST_TAKER_LOCAL_STORAGE_KEYS: {
        PLAYER_CONTRAST: testTakerLocalStorageKeys.PLAYER_CONTRAST,
        PLAYER_ZOOM_FACTOR: testTakerLocalStorageKeys.PLAYER_ZOOM_FACTOR,
        AUDIO_POSITION: testTakerLocalStorageKeys.AUDIO_POSITION,
    },

    /**
     * UI_OPTIONS_DATA
     *
     * Metadata describing how each UI option behaves.
     *
     * Each entry contains:
     * - `key`: the property name to store in localStorage
     * - `defaultValue`: returned if no saved value exists
     * - `localStorageKey`: key used for per-user localStorage storage
     *
     * The structure is intentionally explicit so that UI options can grow
     * without requiring coupling to UI_OPTIONS_LIST.
     *
     * In order to add a configuration to the UI_OPTIONS_DATA, add the corresponding key
     * of that ui option to UI_OPTIONS_LIST, and then use that as the key to define the
     * config for that option.
    */

    UI_OPTIONS_DATA: {
        [UI_OPTIONS_LIST.CONTRAST_OPTION]: {
            key: 'contrastOption',
            defaultValue: contrastOptions.NORMAL,
            localStorageKey: testTakerLocalStorageKeys.PLAYER_CONTRAST,
        },
        [UI_OPTIONS_LIST.ZOOM_FACTOR]: {
            key: 'zoomFactor',
            defaultValue: zoomFactors.SMALL,
            localStorageKey: testTakerLocalStorageKeys.PLAYER_ZOOM_FACTOR,
        },
    },
};
